// ChooseCup phase — "Chọn ly mì" + "Chọn nhân vật bạn muốn".
// Khi bấm Tiếp tục → gọi POST /v1/game/start → lưu session_id vào ctx.

import type { Phase, Theme } from "../state/types";
import { mountFrame, clearChildren } from "../ui/overlay";
import { gameStart, ApiHttpError } from "../api/client";
import { initCrypto, isCryptoReady, getSignature } from "../crypto";

const FLOW = "/textures/flow";
const THEMES: Theme[] = [0, 1, 2, 3, 4];

const TITLE_BAR_FRAC = 0.16;
const LOGO_WIDTH = "86%";
const PANEL_WIDTH = "88%";
const THEME_PANEL_WIDTH = PANEL_WIDTH;
const GENDER_PANEL_WIDTH = PANEL_WIDTH;

let cleanup: (() => void) | null = null;

export const ChooseCup: Phase = {
  enter(ctx, transition) {
    const root = document.getElementById("app")!;
    clearChildren(root);

    const bg = document.createElement("div");
    bg.className = "app-bg";
    bg.style.backgroundImage = "url(/textures/bg_indexA.png)";
    bg.style.backgroundColor = "#f7c7d8";

    const frame = document.createElement("div");
    frame.className = "app-frame";
    frame.style.position = "relative";
    frame.style.display = "flex";
    frame.style.flexDirection = "column";
    frame.style.alignItems = "center";
    frame.style.padding = "1.5% 4% 4%";
    frame.style.boxSizing = "border-box";
    frame.style.gap = "1%";

    const contentPad = `${TITLE_BAR_FRAC * 100}% 6% 4%`;

    frame.innerHTML = `
      <img src="${FLOW}/LogoHeader.png" alt="" style="width:${LOGO_WIDTH};object-fit:contain;flex:0 0 auto;"/>

      <div id="themePanel" style="
        position:relative;width:${THEME_PANEL_WIDTH};aspect-ratio:1500/1472;flex:0 0 auto;
        background:url(${FLOW}/frameTheme.png) center/100% 100% no-repeat;">
        <div id="cups" style="position:absolute;inset:0;padding:${contentPad};box-sizing:border-box;
          display:flex;flex-wrap:wrap;align-content:center;justify-content:center;gap:4%;"></div>
      </div>

      <div id="genderPanel" style="
        position:relative;width:${GENDER_PANEL_WIDTH};aspect-ratio:1500/932;flex:0 0 auto;
        background:url(${FLOW}/frameGender.png) center/100% 100% no-repeat;">
        <div id="genders" style="position:absolute;inset:0;padding:${contentPad};box-sizing:border-box;
          display:flex;align-items:center;justify-content:center;gap:14%;"></div>
      </div>

      <button id="continue" disabled style="
        margin-top:auto;width:55%;background:none;border:0;padding:0;cursor:pointer;opacity:0.5;
        background-image:url(${FLOW}/buttonContinue.png);background-size:contain;background-repeat:no-repeat;
        background-position:center;aspect-ratio:612/208;transition:opacity 120ms;" aria-label="Tiếp tục"></button>
    `;

    root.appendChild(bg);
    const frameCleanup = mountFrame(root, frame);
    cleanup = () => {
      if (bg.parentElement === root) root.removeChild(bg);
      frameCleanup();
    };

    const cupsEl = frame.querySelector<HTMLElement>("#cups")!;
    const gendersEl = frame.querySelector<HTMLElement>("#genders")!;
    const continueBtn = frame.querySelector<HTMLButtonElement>("#continue")!;

    let pickedTheme: Theme = ctx.theme ?? 0;
    let pickedMale: boolean | null = ctx.isMale;

    const refreshContinue = () => {
      const ready = pickedMale !== null;
      continueBtn.disabled = !ready;
      continueBtn.style.opacity = ready ? "1" : "0.5";
    };

    const cupTiles: HTMLButtonElement[] = [];
    THEMES.forEach((t) => {
      const tile = document.createElement("button");
      tile.dataset.theme = String(t);
      tile.style.cssText = `width:28%;aspect-ratio:384/480;background:none;border:0;padding:0;cursor:pointer;position:relative;`;
      tile.innerHTML = `
        <div class="tilebg" style="position:absolute;inset:0;background:url(${FLOW}/frameCup.png) center/100% 100% no-repeat;"></div>
        <img src="/textures/body_cup_${t}.png" alt="" style="position:absolute;inset:12%;width:76%;height:76%;object-fit:contain;"/>
      `;
      tile.addEventListener("click", () => selectTheme(t));
      cupsEl.appendChild(tile);
      cupTiles.push(tile);
    });

    const selectTheme = (t: Theme) => {
      pickedTheme = t;
      cupTiles.forEach((tile) => {
        const on = tile.dataset.theme === String(t);
        const bgEl = tile.querySelector<HTMLElement>(".tilebg")!;
        bgEl.style.backgroundImage = `url(${FLOW}/${on ? "frameCupSelected" : "frameCup"}.png)`;
      });
      refreshContinue();
    };

    const genderTiles: HTMLButtonElement[] = [];
    const GENDERS: { male: boolean; icon: string }[] = [
      { male: true, icon: "iconMale" },
      { male: false, icon: "iconFemale" },
    ];
    GENDERS.forEach(({ male, icon }) => {
      const tile = document.createElement("button");
      tile.dataset.male = String(male);
      tile.style.cssText = `width:34%;aspect-ratio:384/400;background:none;border:0;padding:0;cursor:pointer;position:relative;`;
      tile.innerHTML = `
        <div class="gbg" style="position:absolute;inset:0;background:none;"></div>
        <img src="${FLOW}/${icon}.png" alt="" style="position:absolute;inset:10%;width:80%;height:80%;object-fit:contain;"/>
      `;
      tile.addEventListener("click", () => selectGender(male));
      gendersEl.appendChild(tile);
      genderTiles.push(tile);
    });

    const selectGender = (male: boolean) => {
      pickedMale = male;
      genderTiles.forEach((tile) => {
        const on = tile.dataset.male === String(male);
        const gbg = tile.querySelector<HTMLElement>(".gbg")!;
        gbg.style.backgroundImage = on ? `url(${FLOW}/frameSelected.png)` : "none";
        gbg.style.backgroundSize = "100% 100%";
        gbg.style.backgroundRepeat = "no-repeat";
      });
      refreshContinue();
    };

    selectTheme(pickedTheme);
    if (pickedMale !== null) selectGender(pickedMale);
    refreshContinue();

    continueBtn.addEventListener("click", async () => {
      if (continueBtn.disabled || pickedMale === null) return;
      ctx.theme = pickedTheme;
      ctx.isMale = pickedMale;

      // Đợi wasm load xong trước khi tạo session
      if (!isCryptoReady()) {
        continueBtn.style.pointerEvents = "none";
        continueBtn.style.opacity = "0.6";
        try {
          await initCrypto();
        } catch {
          // wasm fail → vẫn tiếp tục, BE sẽ dùng SIGNATURE_ENABLED=false
        }
        continueBtn.style.pointerEvents = "auto";
        continueBtn.style.opacity = "1";
      }

      // Tạo game session trên BE
      continueBtn.style.pointerEvents = "none";
      continueBtn.style.opacity = "0.6";
      try {
        const res = await gameStart({
          uid: ctx.uid,
          cupId: pickedTheme,
          isMale: pickedMale,
          useCustomFace: null,
          winnerId: ctx.winnerId ?? undefined,
          token: ctx.token ?? undefined,
        });
        ctx.sessionId = res.session_id;
        ctx.signature = await getSignature(res.session_id);
      } catch (err) {
        console.error("[ChooseCup] gameStart failed", err);
        continueBtn.style.pointerEvents = "auto";
        continueBtn.style.opacity = "1";

        // Hiện thông báo lỗi
        let msg = "Có lỗi xảy ra. Vui lòng thử lại.";
        if (err instanceof ApiHttpError) {
          switch (err.status) {
            case 403: msg = "Lượt chơi không hợp lệ hoặc đã được sử dụng."; break;
            case 429: msg = "Quá nhiều yêu cầu. Vui lòng chờ và thử lại."; break;
            case 503: msg = "Máy chủ đang bận. Vui lòng thử lại sau."; break;
          }
        } else if (err instanceof TypeError && err.message.includes("fetch")) {
          msg = "Mất kết nối mạng. Vui lòng kiểm tra internet.";
        }

        // Hiện modal lỗi
        const overlay = document.createElement("div");
        overlay.style.cssText = "position:fixed;inset:0;z-index:999;background:#0008;display:flex;align-items:center;justify-content:center;padding:24px;";
        overlay.innerHTML = `
          <div style="background:#fff;border-radius:14px;padding:24px;max-width:320px;text-align:center;display:flex;flex-direction:column;gap:16px;">
            <div style="font-size:16px;font-weight:bold;color:#333;">Không thể bắt đầu game</div>
            <div style="font-size:14px;color:#666;line-height:1.5;">${msg}</div>
            <button id="err-ok" style="padding:12px;border:0;border-radius:10px;background:#e34;color:#fff;font-weight:bold;cursor:pointer;">OK</button>
          </div>`;
        document.body.appendChild(overlay);
        overlay.querySelector("#err-ok")!.addEventListener("click", () => overlay.remove());
        return;
      }

      transition("ChooseCharacter");
    });
  },
  exit() {
    cleanup?.();
    cleanup = null;
  },
};
